import { useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import Slide from '@/components/shared/Slide';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import {
  ActionChip,
  DifficultyChip,
  EntryPathChip,
  ObjectiveChip,
  StatusChip,
} from '@/components/shared/Chip';
import { sortByPriority } from '@/lib/scoring';
import { useDisplayOptions } from '@/lib/display-options';
import { cn } from '@/lib/utils';
import type { GrantProgram, ViewMode } from '@/types/roadmap.types';

interface Props {
  programs: GrantProgram[];
  mode: ViewMode;
}

type SortField = 'priority' | 'program_name' | 'category' | 'go_no_go';

const goOrder: Record<string, number> = { primary: 0, go: 1, conditional: 2, monitor: 3, no_go: 4 };

export default function ProgramTableSlide({ programs, mode }: Props) {
  const { showObjective, showEntryPath } = useDisplayOptions();
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortAsc, setSortAsc] = useState(true);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sorted = sortField === 'priority'
    ? (sortAsc ? sortByPriority(programs) : sortByPriority(programs).reverse())
    : [...programs].sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
          case 'program_name':
            cmp = a.program_name.localeCompare(b.program_name);
            break;
          case 'category':
            cmp = a.category.localeCompare(b.category);
            break;
          case 'go_no_go':
            cmp = goOrder[a.go_no_go] - goOrder[b.go_no_go];
            break;
        }
        return sortAsc ? cmp : -cmp;
      });

  function SortHeader({
    field,
    children,
    className,
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) {
    return (
      <TableHead className={className}>
        <button
          className="flex items-center gap-1 hover:text-foreground transition-colors no-print"
          onClick={() => toggleSort(field)}
        >
          {children}
          <ArrowUpDown className={cn('w-3 h-3', sortField === field ? 'text-primary' : 'opacity-30')} />
        </button>
        <span className="hidden print:inline">{children}</span>
      </TableHead>
    );
  }

  return (
    <Slide mode={mode} wide>
      <div className="w-full max-w-[1400px] mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Program Overview
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-8">
          Program List
        </h2>

        <Table>
          <TableHeader>
            <TableRow className="border-b-2 border-primary/10 hover:bg-transparent">
              <SortHeader field="program_name">Program</SortHeader>
              <TableHead>Award</TableHead>
              <TableHead>Deadline</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Difficulty</TableHead>
              {showObjective && (
                <SortHeader field="category" className="text-center">
                  Objective
                </SortHeader>
              )}
              {showEntryPath && <TableHead className="text-center">Entry Path</TableHead>}
              <SortHeader field="go_no_go" className="text-center">
                Action
              </SortHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">
                  <div>{p.program_name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{p.grantor}</div>
                </TableCell>
                <TableCell className="text-xs">{p.award_amount.display}</TableCell>
                <TableCell
                  className={cn(
                    'text-xs',
                    p.deadline.type === 'rolling' && 'text-blue-600',
                  )}
                >
                  {p.deadline.display}
                </TableCell>
                <TableCell className="text-center">
                  <StatusChip value={p.deadline.type} />
                </TableCell>
                <TableCell className="text-center">
                  {p.score && <DifficultyChip burdenScore={p.score.application_burden} />}
                </TableCell>
                {showObjective && (
                  <TableCell className="text-center">
                    <ObjectiveChip value={p.investment_layer} />
                  </TableCell>
                )}
                {showEntryPath && (
                  <TableCell className="text-center">
                    <EntryPathChip value={p.entry_path} />
                  </TableCell>
                )}
                <TableCell className="text-center">
                  <ActionChip value={p.go_no_go} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Slide>
  );
}
